import type { BankTransfer } from './bank-transfers-client';
import type { DiscountRewardItem, DiscountType } from './discounts-client';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ApiOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body } = options;
    const url = `${API_BASE_URL}${endpoint}`;

    const fetchOptions: RequestInit = {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    };

    if (body && method !== 'GET') {
        fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    if (response.status === 401 || response.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
        throw new Error(`Unauthorized (${response.status})`);
    }

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error (${response.status}): ${errorText}`);
    }

    return response.json();
}

export type OrderStatus = 'draft' | 'pending' | 'confirmed' | 'completed' | 'pending_payment' | 'pos_push_failed' | 'pushed_to_external_pending' | 'pushed_to_external' | 'cancelled' | 'abandoned';

export interface OrderItemComment {
    text: string;
    quantity: number;
}

export interface OrderItemOption {
    /** Absent on lines recorded before the ids were persisted; the cart editor drops those. */
    groupId?: string;
    optionId?: string;
    groupName: string;
    optionName: string;
    priceDelta: number;
}

export interface OrderItem {
    catalogItemId: string;
    name: string;
    quantity: number;
    unitPrice: number;
    options?: OrderItemOption[];
    comments?: OrderItemComment[] | string;
    subtotal: number;
}

export interface OrderCustomer {
    id: string;
    phone: string;
    name?: string;
}

export interface OrderPaymentAttempt {
    at: string;
    method: 'saved_card' | 'payment_link';
    event: 'approved' | 'declined' | 'failed' | 'link_generated' | 'paid' | 'refunded';
    /** Entries sharing a paymentId (or linkId) are lifecycle events of the same attempt. */
    paymentId?: string | null;
    linkId?: string | null;
    detail?: string | null;
}

/**
 * The coupon as it was when the order confirmed, frozen onto the order itself.
 *
 * Snapshotted rather than referenced on purpose: a merchant editing or disabling the coupon later
 * must not change what an already-confirmed order was charged, so this is what the UI must render —
 * never the current coupon definition.
 */
export interface AppliedCoupon {
    discountId: string;
    code: string;
    type: DiscountType;
    value: number;
    rewardItems?: DiscountRewardItem[] | null;
    excludePromoItems: boolean;
    minimumOrderAmount?: number | null;
    /** Money actually taken off, already clamped to the items subtotal. */
    discountAmount: number;
}

export interface Order {
    id: string;
    brandId: string;
    customerId: string;
    customer?: OrderCustomer;
    conversationId?: string;
    status: OrderStatus;
    items: OrderItem[];
    deliveryType?: 'delivery' | 'pickup';
    deliveryAddress?: Record<string, unknown>;
    deliveryFee?: number;
    estimatedDeliveryMinutes?: number;
    locationId?: string;
    paymentPreference?: 'cash' | 'send_pos_for_card' | 'bank_transfer' | 'mercado_pago' | 'card' | 'pay_at_store';
    /** The order's ACTIVE transfer proof (latest non-superseded `bank_transfer` row). */
    bankTransfer?: BankTransfer;
    /** Tip captured in the checkout flow. Already folded into `total`. */
    tipAmount?: number;
    /**
     * Discount discount already subtracted from `total`. 0 when no coupon is applied — and also when a
     * coupon IS applied but currently takes nothing off (its reward item is not in the cart), which
     * is why `appliedDiscount` must be checked separately rather than inferred from this being > 0.
     */
    discountAmount?: number;
    appliedDiscount?: AppliedCoupon;
    total: number;
    currency: string;
    notes?: string;
    cancelledReason?: string;
    confirmedAt?: string;
    posError?: string;
    /** True when posError came from a POS 4xx — deterministic, retrying the push cannot succeed. */
    posErrorPermanent?: boolean;
    isSandbox?: boolean;
    trackingUrl?: string;
    paymentLinkUrl?: string;
    /** Menteo payment link collecting this order. */
    menteoPaymentLinkId?: string;
    /** Paid Menteo payment id — deep-links to the dashboard payment detail. */
    menteoPaymentId?: string;
    /** Loyalty member linked on the paid checkout — deep-links to the dashboard profile. */
    loyaltyCustomerId?: string;
    paymentDeadlineAt?: string;
    paymentConfirmedAt?: string;
    paymentAttempts?: OrderPaymentAttempt[];
    externalPosOrderId?: string;
    externalPosProvider?: string;
    createdAt: string;
    updatedAt: string;
}

interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

export const VALID_NEXT_STATUSES: Record<OrderStatus, OrderStatus[]> = {
    draft: ['pending', 'pending_payment', 'pushed_to_external_pending', 'pushed_to_external', 'cancelled', 'abandoned'],
    pending: ['confirmed', 'cancelled'],
    confirmed: ['completed', 'cancelled'],
    completed: [],
    // Payment (not the operator) advances a pending_payment order — only cancel is manual.
    pending_payment: ['cancelled'],
    // Paid but the POS push failed — the operator resolves it: fulfill manually (→ pending), mark
    // as keyed into the POS by hand (→ pushed_to_external), or cancel (+ refund from the dashboard).
    pos_push_failed: ['pending', 'pushed_to_external', 'cancelled'],
    pushed_to_external_pending: ['pushed_to_external', 'cancelled'],
    pushed_to_external: [],
    cancelled: [],
    abandoned: [],
};

export const STATUS_LABELS: Record<OrderStatus, string> = {
    draft: 'Borrador',
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    completed: 'Completado',
    pending_payment: 'Esperando pago',
    pos_push_failed: 'Pagado — error POS',
    pushed_to_external_pending: 'Pendiente de pago',
    pushed_to_external: 'Enviado a POS',
    cancelled: 'Cancelado',
    abandoned: 'Abandonado',
};

export const STATUS_COLORS: Record<OrderStatus, string> = {
    draft: 'bg-muted text-muted-foreground',
    pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
    confirmed: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    completed: 'bg-emerald-200 dark:bg-emerald-900/50 text-emerald-900 dark:text-emerald-200',
    pending_payment: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
    pos_push_failed: 'bg-red-600 text-white',
    pushed_to_external_pending: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
    pushed_to_external: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    cancelled: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    abandoned: 'bg-muted text-muted-foreground',
};

export function getForwardStatus(order: Order): OrderStatus | null {
    // pos_push_failed has SEVERAL manual resolutions — the detail panel's action card owns them.
    if (order.status === 'pos_push_failed') return null;
    const next = VALID_NEXT_STATUSES[order.status].filter((s) => s !== 'cancelled');
    if (next.length === 0) return null;
    return next[0];
}

export function getForwardActionLabel(order: Order): string | null {
    switch (order.status) {
        case 'pending':
            return 'Confirmar';
        case 'confirmed':
            return 'Completar';
        default:
            return null;
    }
}

export function canCancel(status: OrderStatus): boolean {
    return VALID_NEXT_STATUSES[status].includes('cancelled');
}

export interface ItemSales {
    catalogItemId: string;
    /** Name the product was sold under (from the order lines) — survives catalog re-syncs. */
    name: string;
    locationId: string | null;
    totalQuantity: number;
    orderCount: number;
}

export const ordersClient = {
    async list(params?: { brandId?: string; status?: OrderStatus }): Promise<ApiResponse<Order[]>> {
        const searchParams = new URLSearchParams();
        if (params?.brandId) searchParams.set('brandId', params.brandId);
        if (params?.status) searchParams.set('status', params.status);
        const query = searchParams.toString();
        return api<ApiResponse<Order[]>>(`/orders${query ? `?${query}` : ''}`);
    },

    async getToday(params: { locationId?: string; brandId?: string; from?: string; to?: string; search?: string }): Promise<ApiResponse<Order[]>> {
        const searchParams = new URLSearchParams();
        if (params.locationId) searchParams.set('locationId', params.locationId);
        if (params.brandId) searchParams.set('brandId', params.brandId);
        if (params.from) searchParams.set('from', params.from);
        if (params.to) searchParams.set('to', params.to);
        if (params.search) searchParams.set('search', params.search);
        const query = searchParams.toString();
        return api<ApiResponse<Order[]>>(`/orders/today${query ? `?${query}` : ''}`);
    },

    async getById(id: string): Promise<ApiResponse<Order>> {
        return api<ApiResponse<Order>>(`/orders/${id}`);
    },

    /** Units sold per catalog item and location across the brand's placed orders. */
    async getItemSales(params?: { days?: number; locationId?: string }): Promise<ApiResponse<ItemSales[]>> {
        const searchParams = new URLSearchParams();
        if (params?.days) searchParams.set('days', String(params.days));
        if (params?.locationId) searchParams.set('locationId', params.locationId);
        const query = searchParams.toString();
        return api<ApiResponse<ItemSales[]>>(`/orders/item-sales${query ? `?${query}` : ''}`);
    },

    async getByConversation(conversationId: string): Promise<ApiResponse<Order[]>> {
        return api<ApiResponse<Order[]>>(`/orders/conversation/${conversationId}`);
    },

    /** Short-lived presigned URL for the transfer receipt stored in S3 (orders paid by transfer). */
    async getTransferReceiptUrl(id: string): Promise<ApiResponse<{ url: string; expiresIn: number }>> {
        return api<ApiResponse<{ url: string; expiresIn: number }>>(`/orders/${id}/transfer-receipt-url`);
    },

    async updateStatus(id: string, status: OrderStatus, cancelledReason?: string): Promise<ApiResponse<Order>> {
        return api<ApiResponse<Order>>(`/orders/${id}/status`, {
            method: 'PATCH',
            body: { status, cancelledReason },
        });
    },

    /**
     * Retry the POS push of a paid order parked in pos_push_failed. Always answers 200 with the
     * reloaded order — read its status to know whether the push healed or it stays parked.
     */
    async retryPosPush(id: string): Promise<ApiResponse<Order>> {
        return api<ApiResponse<Order>>(`/orders/${id}/retry-pos-push`, { method: 'POST' });
    },
};
